/*
  Otaku TCG live draw: countdown, spinning wheel and provably fair winner.
  Ported from Otaku Kulture's giveaway page (sections/giveaways.liquid, October 2026).
  The winner of each draw comes from the drand "quicknet" public randomness beacon: the first
  round published at or after the draw time, so every visitor computes the same winner and
  nobody can know it in advance. Winner = randomness mod the number of entries in the pool.
  A re-draw leaves out every line of the earlier winner (one person can hold several entries).
*/
(function () {
  'use strict';
  var CHAIN = '52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971';
  var GENESIS = 1692803367; // drand quicknet genesis_time (unix seconds), from /info
  var PERIOD = 3; // drand quicknet period (seconds), from /info
  var HOSTS = ['https://api.drand.sh', 'https://api2.drand.sh', 'https://api3.drand.sh', 'https://drand.cloudflare.com'];
  var LIVE_GRACE_MS = 120 * 1000; // a load within this long after a draw still gets the live spin
  var FEATURE_MS = 48 * 3600 * 1000; // a drawn giveaway stays full size for this long
  var TZ = 'Africa/Johannesburg';
  var COLORS = ['#ff0073', '#6a33d8', '#16101d'];
  var HEAD_FONT = (getComputedStyle(document.documentElement).getPropertyValue('--font-heading-family') || '').trim() || 'system-ui';
  var BODY_FONT = (getComputedStyle(document.documentElement).getPropertyValue('--font-body-family') || '').trim() || 'system-ui';
  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* ---------- helpers ---------- */
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function fmtWhen(ms) {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date(ms));
      var p = {};
      parts.forEach(function (x) { p[x.type] = x.value; });
      return p.weekday + ' ' + p.day + ' ' + p.month + ', ' + p.hour + ':' + p.minute;
    } catch (e) { return new Date(ms).toUTCString(); }
  }
  function fmtDate(ms) {
    try { return new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(ms)); }
    catch (e) { return new Date(ms).toDateString(); }
  }
  function parseTime(s) {
    var t = Date.parse(String(s || '').trim());
    return isNaN(t) ? null : t;
  }
  // First drand round published at or after time ms (round r is published at GENESIS + (r - 1) * PERIOD).
  function roundFor(ms) {
    var t = Math.floor(ms / 1000);
    return Math.max(1, Math.ceil((t - GENESIS) / PERIOD) + 1);
  }
  function roundUrl(round, host) { return (host || HOSTS[0]) + '/' + CHAIN + '/public/' + round; }

  var roundCache = {};
  function fetchJson(url, ms) {
    var ctl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, ms);
    return fetch(url, { cache: 'no-store', signal: ctl ? ctl.signal : undefined, credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .finally(function () { clearTimeout(timer); });
  }
  // Resolves with the round's beacon once it exists. Retries every 2 s forever; onSlow fires after 60 s.
  function getRound(round, onSlow) {
    if (roundCache[round]) return roundCache[round];
    var p = new Promise(function (resolve) {
      var started = Date.now();
      var slowSent = false;
      var hostStart = 0;
      function attempt() {
        var i = 0;
        function next() {
          if (i >= HOSTS.length) {
            if (!slowSent && Date.now() - started > 60000) { slowSent = true; if (onSlow) onSlow(); }
            setTimeout(attempt, 2000);
            return;
          }
          var host = HOSTS[(hostStart + i) % HOSTS.length];
          i++;
          fetchJson(roundUrl(round, host), 6000).then(function (j) {
            if (j && Number(j.round) === round && /^[0-9a-f]{64}$/i.test(j.randomness || '')) {
              resolve({ round: round, randomness: j.randomness.toLowerCase(), url: roundUrl(round, HOSTS[0]) });
            } else { next(); }
          }).catch(function () { next(); });
        }
        next();
      }
      attempt();
    });
    roundCache[round] = p;
    return p;
  }
  function pickIndex(randomness, n) { return Number(BigInt('0x' + randomness) % BigInt(n)); }
  function sha256Hex(text) {
    if (!window.crypto || !crypto.subtle || !window.TextEncoder) return Promise.resolve('(needs a secure https page)');
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)).then(function (buf) {
      return Array.prototype.map.call(new Uint8Array(buf), function (b) { return (b < 16 ? '0' : '') + b.toString(16); }).join('');
    });
  }

  function parseGiveaway(raw, i) {
    var entries = String(raw.entries || '').split(/\r?\n/).map(function (s) { return s.trim(); }).filter(Boolean);
    var at = parseTime(raw.draw_at);
    var draws = [];
    if (at != null) {
      draws.push({ label: 'Winner', at: at });
      String(raw.rerolls || '').split(/[,\n]/).map(function (s) { return parseTime(s); })
        .filter(function (t) { return t != null && t > at; })
        .sort(function (a, b) { return a - b; })
        .forEach(function (t, k) { draws.push({ label: 'Re-draw ' + (k + 1), at: t }); });
    }
    return {
      id: raw.id || ('g' + i), title: raw.title || 'Giveaway', prize: raw.prize || '', image: raw.image || '',
      rules: raw.rules || '', wa: raw.whatsapp_url || '', entries: entries, drawAt: at, draws: draws,
      lastAt: draws.length ? draws[draws.length - 1].at : null
    };
  }

  // Computes draw k's winner: drand randomness mod the pool size, where the pool is the entries
  // (in listed order) minus the winners of earlier draws.
  function computeDraw(g, k, onSlow) {
    var excluded = {};
    var chain = Promise.resolve();
    for (var j = 0; j < k; j++) {
      (function (j) { chain = chain.then(function () { return computeDraw(g, j).then(function (r) { g.entries.forEach(function (e, idx) { if (e === r.entry) excluded[idx] = true; }); }); }); })(j);
    }
    if (g.draws[k].promise) return g.draws[k].promise;
    g.draws[k].promise = chain.then(function () {
      var pool = [];
      g.entries.forEach(function (e, idx) { if (!excluded[idx]) pool.push(idx); });
      if (!pool.length) throw new Error('no entries');
      var round = roundFor(g.draws[k].at);
      return getRound(round, onSlow).then(function (b) {
        var pi = pickIndex(b.randomness, pool.length);
        return { k: k, round: round, randomness: b.randomness, url: b.url, pool: pool, poolIndex: pi, entryIndex: pool[pi], entry: g.entries[pool[pi]] };
      });
    });
    return g.draws[k].promise;
  }

  /* ---------- sound (only after the visitor unmutes) ---------- */
  function Sound() { this.on = false; this.ctx = null; this.last = 0; }
  Sound.prototype.enable = function (on) {
    this.on = on;
    if (on && !this.ctx) {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (AC) this.ctx = new AC();
    }
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
  };
  Sound.prototype.blip = function (freq, dur, vol, type) {
    if (!this.on || !this.ctx) return;
    var c = this.ctx, o = c.createOscillator(), g = c.createGain(), t = c.currentTime;
    o.type = type || 'square'; o.frequency.value = freq;
    g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(c.destination); o.start(t); o.stop(t + dur + 0.02);
  };
  Sound.prototype.tick = function () {
    var now = performance.now();
    if (now - this.last < 35) return;
    this.last = now;
    this.blip(1400, 0.03, 0.05);
  };
  Sound.prototype.fanfare = function () {
    var self = this;
    [523, 659, 784, 1047].forEach(function (f, i) { setTimeout(function () { self.blip(f, 0.25, 0.08, 'triangle'); }, i * 120); });
  };

  /* ---------- wheel ---------- */
  function Wheel(canvas, onIndex) {
    this.c = canvas; this.ctx = canvas.getContext('2d'); this.onIndex = onIndex;
    this.n = 1; this.placeholder = false; this.theta = 0; this.mode = 'idle'; this.omega = reduced ? 0 : 0.25; this.lastIdx = -1;
    this.anim = null; this.lastT = 0; this.size = 0;
    var self = this;
    this.frame = function (t) { self.step(t); self.raf = requestAnimationFrame(self.frame); };
    this.raf = requestAnimationFrame(this.frame);
  }
  Wheel.prototype.setCount = function (n) { this.n = Math.max(1, n); this.size = 0; this.lastIdx = -1; };
  Wheel.prototype.indexAt = function (theta) {
    var TAU = Math.PI * 2, seg = TAU / this.n;
    var a = ((-theta % TAU) + TAU) % TAU;
    return Math.min(this.n - 1, Math.floor(a / seg));
  };
  Wheel.prototype.targetFor = function (idx, from) {
    var TAU = Math.PI * 2, seg = TAU / this.n;
    var want = -((idx + 0.5) * seg); // theta that puts the middle of segment idx under the pointer
    var d = ((want - from) % TAU + TAU) % TAU;
    return from + d;
  };
  Wheel.prototype.spinFast = function () {
    this.anim = null; this.mode = 'fast';
    if (reduced) { this.omega = 0; }
  };
  Wheel.prototype.idle = function () { this.anim = null; this.mode = 'idle'; this.omega = reduced ? 0 : 0.25; };
  Wheel.prototype.stopAt = function (idx) {
    this.anim = null; this.mode = 'stopped'; this.omega = 0;
    this.theta = this.targetFor(idx, 0);
    this.draw(); this.report();
  };
  // Decelerates smoothly (cubic ease-out, matching the current speed) so segment idx stops under the pointer.
  Wheel.prototype.landOn = function (idx) {
    var self = this;
    return new Promise(function (resolve) {
      var w0 = Math.max(self.omega, reduced ? 0 : 6);
      if (reduced || w0 < 0.5) {
        var from = self.theta, to = self.targetFor(idx, from + Math.PI * 2);
        self.anim = { t0: performance.now(), from: from, d: to - from, T: reduced ? 900 : 2500, resolve: resolve, ease: 'inout' };
      } else {
        var base = w0 * 6.5 / 3; // radians covered by a 6.5 s ease-out starting at speed w0
        var to2 = self.targetFor(idx, self.theta + base);
        var d = to2 - self.theta;
        self.anim = { t0: performance.now(), from: self.theta, d: d, T: 3000 * d / w0, resolve: resolve, ease: 'out' };
      }
      self.mode = 'landing';
      // Finish even if animation frames are paused (background tab), so the result always shows.
      var a = self.anim;
      setTimeout(function () {
        if (self.anim !== a) return;
        self.anim = null; self.theta = a.from + a.d; self.mode = 'stopped'; self.omega = 0;
        self.draw(); self.report(); a.resolve();
      }, a.T + 400);
    });
  };
  Wheel.prototype.step = function (t) {
    var dt = this.lastT ? Math.min(0.1, (t - this.lastT) / 1000) : 0;
    this.lastT = t;
    if (this.anim) {
      var a = this.anim, u = Math.min(1, (t - a.t0) / a.T);
      var e = a.ease === 'out' ? 1 - Math.pow(1 - u, 3) : (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);
      this.theta = a.from + a.d * e;
      if (u >= 1) { this.anim = null; this.mode = 'stopped'; this.omega = 0; a.resolve(); }
    } else if (this.mode === 'fast') {
      if (!reduced) this.omega = Math.min(13, this.omega + 9 * dt);
      this.theta += this.omega * dt;
    } else if (this.mode === 'idle') {
      this.theta += this.omega * dt;
    }
    this.draw();
    this.report();
  };
  Wheel.prototype.report = function () {
    var i = this.indexAt(this.theta);
    if (i !== this.lastIdx) { this.lastIdx = i; if (this.onIndex) this.onIndex(i, this.mode); }
  };
  Wheel.prototype.draw = function () {
    var c = this.c, rect = c.getBoundingClientRect();
    if (!rect.width) return;
    var dpr = Math.min(2, window.devicePixelRatio || 1), px = Math.round(rect.width * dpr);
    if (c.width !== px || this.size !== px) { c.width = px; c.height = px; this.size = px; }
    var ctx = this.ctx, r = px / 2, n = this.n, seg = Math.PI * 2 / n;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, px, px);
    ctx.translate(r, r);
    ctx.rotate(this.theta - Math.PI / 2);
    var ncol = n % 2 === 0 ? 2 : 3;
    var pal = ncol === 2 ? [COLORS[0], COLORS[1]] : [COLORS[0], COLORS[1], COLORS[2]];
    // Past about 48 slices, single-slice stripes shimmer on a phone: colour them in blocks instead.
    var band = n > 48 ? Math.ceil(n / 16) : 1, nbands = Math.ceil(n / band);
    if (band > 1) { ncol = nbands % 2 === 0 ? 2 : 3; pal = ncol === 2 ? [COLORS[0], COLORS[1]] : [COLORS[0], COLORS[1], COLORS[2]]; }
    for (var i = 0; i < n; i++) {
      var col = pal[Math.floor(i / band) % ncol];
      if (band === 1 && i === n - 1 && n > 1 && col === pal[0]) col = pal[1];
      ctx.beginPath(); ctx.moveTo(0, 0);
      ctx.arc(0, 0, r, i * seg, (i + 1) * seg + (n > 1 ? 0.002 : 0));
      ctx.closePath(); ctx.fillStyle = n === 1 ? COLORS[0] : col; ctx.fill();
      if (i % 10 === 0 && n > 20 && !this.placeholder) {
        ctx.save(); ctx.rotate((i + 0.5) * seg);
        ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.fillRect(r * 0.86, -1, r * 0.1, 2);
        ctx.restore();
      }
    }
    // entry number on each slice, matching the entries list (owner, 16 Oct)
    if (n > 1 && !this.placeholder) {
      // Numbers too small to read become a dotted ring, so a big wheel labels every 5th or 10th slice.
      var every = n > 150 ? 10 : n > 60 ? 5 : 1;
      var lr = r * 0.84, fs = Math.max(5, Math.min(r * 0.075, lr * seg * every * 0.8));
      ctx.fillStyle = '#ffffff'; ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
      ctx.font = '700 ' + fs.toFixed(1) + 'px ' + BODY_FONT;
      for (var j = 0; j < n; j++) {
        if ((j + 1) % every !== 0 && every > 1) continue;
        ctx.save(); ctx.rotate((j + 0.5) * seg);
        ctx.fillText(String(this.labels && this.labels[j] != null ? this.labels[j] : j + 1), lr, 0);
        ctx.restore();
      }
    }
    // rim dots
    ctx.fillStyle = '#ffffff';
    for (var k = 0; k < 24; k++) {
      var a = k * Math.PI * 2 / 24;
      ctx.beginPath(); ctx.arc(Math.cos(a) * r * 0.955, Math.sin(a) * r * 0.955, r * 0.018, 0, Math.PI * 2); ctx.fill();
    }
    // the entry under the pointer, big and upright, so it reads on a phone while the wheel turns (owner, 16 Oct)
    if (n > 1 && !this.placeholder) {
      var cur = this.indexAt(this.theta);
      var lab = '#' + (this.labels && this.labels[cur] != null ? this.labels[cur] : cur + 1);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      var bf = Math.round(r * 0.14), bh = Math.round(bf * 1.5), bw = Math.round(bf * (lab.length * 0.62 + 1.2));
      var bx = r - bw / 2, by = Math.round(r * 0.17);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(bx, by, bw, bh, bh / 2); else ctx.rect(bx, by, bw, bh);
      ctx.fill();
      ctx.fillStyle = '#16101d'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = '400 ' + bf + 'px ' + HEAD_FONT;
      ctx.fillText(lab, r, by + bh / 2 + 1);
    }
  };

  /* ---------- confetti ---------- */
  function confetti(host) {
    if (reduced) return;
    var cv = el('canvas', 'otcg-draw-confetti');
    host.appendChild(cv);
    var ctx = cv.getContext('2d'), w = cv.width = host.clientWidth, h = cv.height = host.clientHeight;
    var cols = ['#ff0073', '#6a33d8', '#ffffff', '#ff7ac1', '#4fd1ff'], ps = [];
    for (var i = 0; i < 180; i++) {
      ps.push({ x: w / 2 + (Math.random() - 0.5) * w * 0.3, y: Math.min(h * 0.45, 420), vx: (Math.random() - 0.5) * 14, vy: -Math.random() * 16 - 4,
        s: 5 + Math.random() * 7, r: Math.random() * 6, vr: (Math.random() - 0.5) * 0.4, c: cols[i % cols.length] });
    }
    var t0 = performance.now();
    (function loop(t) {
      var age = t - t0;
      ctx.clearRect(0, 0, w, h);
      ps.forEach(function (p) {
        p.vy += 0.35; p.vx *= 0.99; p.x += p.vx; p.y += p.vy; p.r += p.vr;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r); ctx.globalAlpha = Math.max(0, 1 - age / 4500);
        ctx.fillStyle = p.c; ctx.fillRect(-p.s / 2, -p.s / 4, p.s, p.s / 2); ctx.restore();
      });
      if (age < 4500) requestAnimationFrame(loop); else cv.remove();
    })(t0);
  }

  /* ---------- one full-size giveaway card ---------- */
  function Card(g, cfg, sound) {
    this.g = g; this.cfg = cfg; this.sound = sound; this.cur = null; this.results = [];
    this.build();
  }
  Card.prototype.build = function () {
    var g = this.g, self = this;
    var root = this.root = el('article', 'otcg-draw-card');
    var top = el('div', 'otcg-draw-card__top');
    this.badge = el('span', 'otcg-draw-badge');
    this.when = el('span', 'otcg-draw-card__when');
    top.appendChild(this.badge); top.appendChild(this.when);
    root.appendChild(top);
    root.appendChild(el('h3', 'otcg-draw-card__title', g.title));
    if (g.prize) {
      var pz = el('p', 'otcg-draw-card__prize');
      pz.appendChild(el('span', null, 'Prize: ')); pz.appendChild(document.createTextNode(g.prize));
      root.appendChild(pz);
    }
    var grid = el('div', 'otcg-draw-grid'), main = el('div', 'otcg-draw-main');
    grid.appendChild(main); root.appendChild(grid);

    // countdown stage with mascot
    var stage = this.stage = el('div', 'otcg-draw-stage');
    this.bubble = el('div', 'otcg-draw-bubble', 'Good luck!');
    if (this.cfg.mascot) {
      var m = el('div', 'otcg-draw-mascot'), img = el('img');
      img.src = this.cfg.mascot; img.alt = ''; img.width = 190; img.height = 190; img.decoding = 'async';
      img.onerror = function () { m.remove(); stage.classList.add('otcg-draw-stage--nomascot'); self.count.insertBefore(self.bubble, self.count.firstChild); self.bubble.classList.add('otcg-draw-bubble-solo'); };
      m.appendChild(this.bubble); m.appendChild(img); stage.appendChild(m);
    } else {
      stage.classList.add('otcg-draw-stage--nomascot');
      this.bubble.classList.add('otcg-draw-bubble-solo');
    }
    var count = this.count = el('div', 'otcg-draw-count');
    if (!this.cfg.mascot) count.appendChild(this.bubble);
    this.countLabel = el('div', 'otcg-draw-count__label', 'The draw starts in');
    var row = el('div', 'otcg-draw-count__row');
    this.units = ['Days', 'Hours', 'Minutes', 'Seconds'].map(function (l) {
      var u = el('div', 'otcg-draw-unit'), n = el('span', 'otcg-draw-unit__n', '00');
      u.appendChild(n); u.appendChild(el('span', 'otcg-draw-unit__l', l)); row.appendChild(u);
      return n;
    });
    count.appendChild(this.countLabel); count.appendChild(row);
    this.status = el('div', 'otcg-draw-status');
    this.status.hidden = true;
    count.appendChild(this.status);
    stage.appendChild(count);
    main.appendChild(stage);

    // wheel
    var wb = this.wheelBox = el('div', 'otcg-draw-wheelbox'), wheel = el('div', 'otcg-draw-wheel'), canvas = el('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    wheel.appendChild(el('div', 'otcg-draw-pointer')); wheel.appendChild(canvas);
    this.hub = el('div', 'otcg-draw-hub');
    wheel.appendChild(this.hub);
    wb.appendChild(wheel);
    this.countChip = el('p', 'otcg-draw-chip');
    this.setHub(g.entries.length);
    wb.appendChild(this.countChip);
    this.current = el('div', 'otcg-draw-current', g.entries[0] || (g.drawAt != null ? 'Spins ' + fmtWhen(g.drawAt) : '—'));
    if (!g.entries.length) this.current.classList.add('otcg-draw-current--wait');
    this.current.setAttribute('aria-live', 'off');
    wb.appendChild(this.current);
    main.appendChild(wb);
    this.pool = g.entries.map(function (_, i) { return i; });
    this.wheel = new Wheel(canvas, function (i, mode) {
      var e = g.entries[self.pool[i]];
      if (e != null) self.current.textContent = e;
      if (mode === 'fast' || mode === 'landing') self.sound.tick();
    });
    this.wheel.placeholder = !this.pool.length;
    this.wheel.setCount(this.pool.length || 24);
    this.wheel.labels = this.pool.map(function (x) { return x + 1; });

    // winner + tools
    this.winBox = el('div'); main.appendChild(this.winBox);
    var tools = el('div', 'otcg-draw-tools');
    this.soundBtn = el('button', 'otcg-draw-btn otcg-draw-btn--ghost', 'Sound off');
    this.soundBtn.type = 'button';
    this.soundBtn.setAttribute('aria-pressed', 'false');
    this.soundBtn.addEventListener('click', function () {
      var on = !self.sound.on; self.sound.enable(on);
      document.querySelectorAll('.otcg-draw [data-otcg-draw-sound]').forEach(function (b) {
        b.textContent = on ? 'Sound on' : 'Sound off'; b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });
    this.soundBtn.setAttribute('data-otcg-draw-sound', '');
    this.replayBtn = el('button', 'otcg-draw-btn', 'Replay the draw');
    this.replayBtn.type = 'button'; this.replayBtn.hidden = true;
    this.replayBtn.addEventListener('click', function () { self.replay(); });
    tools.appendChild(this.soundBtn); tools.appendChild(this.replayBtn);
    main.appendChild(tools);

    // fairness
    var fair = this.fair = el('details', 'otcg-draw-fair');
    fair.appendChild(el('summary', null, 'How this draw is fair'));
    fair.appendChild(el('p', null, 'The winner comes from public randomness that the drand beacon publishes at the draw time, so nobody, including us, could know it beforehand, and everyone who opens this page sees the same winner.'));
    this.fairList = el('dl');
    fair.appendChild(this.fairList);
    main.appendChild(fair);
    this.fp = sha256Hex(g.entries.join('\n'));
    this.renderFair();

    // entries sidebar
    var side = el('aside', 'otcg-draw-side');
    var sh = el('div', 'otcg-draw-side__head');
    sh.appendChild(el('h3', null, 'Entries'));
    sh.appendChild(el('span', 'otcg-draw-side__count', String(g.entries.length)));
    side.appendChild(sh);
    var search = el('input', 'otcg-draw-search'); search.type = 'search'; search.placeholder = 'Last 4 digits of your number';
    search.setAttribute('aria-label', 'Search entries');
    if (g.entries.length) side.appendChild(search);
    if (g.entries.length) side.appendChild(el('p', 'otcg-draw-side__hint', 'Search the last 4 digits of your WhatsApp number to find your entry numbers, then look for them on the wheel.'));
    var ol = this.ol = el('ol', 'otcg-draw-entries');
    this.items = g.entries.map(function (e, i) {
      var li = el('li'); li.appendChild(el('span', null, (i + 1) + '.')); li.appendChild(el('span', null, e)); ol.appendChild(li);
      li._q = e.replace(/\s+/g, '').toLowerCase();
      li._d = e.replace(/\D/g, '').slice(-4); // last 4 digits, the only digits shown after the mask
      return li;
    });
    var none = el('div', 'otcg-draw-side__none', 'No match.'); none.hidden = true;
    side.appendChild(ol); side.appendChild(none);
    if (!g.entries.length) { none.textContent = 'The numbered entry list goes up here before the draw. It shows only the last 4 digits of each number.'; none.hidden = false; }
    search.addEventListener('input', function () {
      var q = search.value.replace(/\s+/g, '').toLowerCase(), shown = 0;
      var dg = q.replace(/\D/g, ''), q4 = dg.length >= 4 ? dg.slice(-4) : dg; // a full number matches on its last 4 digits
      self.items.forEach(function (li) { var ok = !q || li._q.indexOf(q) !== -1 || (q4 && li._d.indexOf(q4) !== -1); li.hidden = !ok; if (ok) shown++; });
      none.hidden = shown > 0 || !g.entries.length;
    });
    grid.appendChild(side);
  };
  Card.prototype.setBadge = function (kind, text) {
    this.badge.className = 'otcg-draw-badge' + (kind ? ' otcg-draw-badge--' + kind : '');
    this.badge.textContent = '';
    if (kind === 'live') this.badge.appendChild(el('span', 'otcg-draw-badge__dot'));
    this.badge.appendChild(document.createTextNode(text));
  };
  Card.prototype.say = function (text) {
    if (this.bubble.textContent === text) return;
    this.bubble.textContent = text;
    this.bubble.style.animation = 'none'; void this.bubble.offsetWidth; this.bubble.style.animation = '';
  };
  Card.prototype.showCountdown = function (k) {
    var d = this.g.draws[k];
    this.stage.hidden = false;
    this.countLabel.textContent = k === 0 ? 'The draw starts in' : d.label + ' in';
    this.status.hidden = true;
    this.count.classList.remove('otcg-draw-count--final');
    this.when.textContent = (k === 0 ? 'Draw: ' : d.label + ': ') + fmtWhen(d.at) + ' SAST';
    this.setBadge(k === 0 ? '' : 'live', k === 0 ? 'Upcoming' : 'Re-draw pending');
    var self = this;
    var prev = [];
    function upd() {
      var ms = Math.max(0, d.at - Date.now()), s = Math.ceil(ms / 1000);
      var vals = [Math.floor(s / 86400), Math.floor(s % 86400 / 3600), Math.floor(s % 3600 / 60), s % 60];
      vals.forEach(function (v, i) {
        var txt = pad(v);
        if (prev[i] !== txt) {
          self.units[i].textContent = txt; prev[i] = txt;
          self.units[i].classList.remove('otcg-draw-tick'); void self.units[i].offsetWidth; self.units[i].classList.add('otcg-draw-tick');
        }
      });
      if (s <= 10) self.count.classList.add('otcg-draw-count--final');
      if (s <= 0) self.say('Here we go!!');
      else if (s <= 60) self.say('Almost time!');
      else if (s <= 600) self.say('Get ready…');
      else if (s <= 3600) self.say('Not long now!');
      else self.say('Good luck!');
    }
    upd();
    return new Promise(function (resolve) {
      var iv = setInterval(function () {
        upd();
        if (Date.now() >= d.at) { clearInterval(iv); resolve(); }
      }, 200);
    });
  };
  Card.prototype.showStatus = function (title, sub) {
    this.status.textContent = title;
    if (sub) this.status.appendChild(el('small', null, sub));
    this.status.hidden = false;
  };
  Card.prototype.setHub = function (n) {
    this.hub.textContent = '';
    if (this.cfg.emblem) { var im = el('img'); im.src = this.cfg.emblem; im.alt = ''; im.width = 128; im.height = 128; im.decoding = 'async'; this.hub.appendChild(im); }
    if (this.countChip) { this.countChip.textContent = n ? n + (n === 1 ? ' entry' : ' entries') : 'The list goes up before the draw'; }
  };
  Card.prototype.usePool = function (pool) {
    this.pool = pool; this.wheel.setCount(pool.length); this.wheel.labels = pool.map(function (x) { return x + 1; }); this.setHub(pool.length);
  };
  Card.prototype.renderFair = function () {
    var dl = this.fairList, self = this, g = this.g;
    dl.textContent = '';
    function row(k, v, href) {
      dl.appendChild(el('dt', null, k));
      var dd = el('dd');
      if (href) { var a = el('a', null, v); a.href = href; a.target = '_blank'; a.rel = 'noopener'; dd.appendChild(a); } else dd.textContent = v;
      dl.appendChild(dd);
    }
    row('Entries', String(g.entries.length) + ', in the order listed');
    var fpDd;
    dl.appendChild(el('dt', null, 'List fingerprint (SHA-256)'));
    fpDd = el('dd', null, '…'); dl.appendChild(fpDd);
    this.fp.then(function (h) { fpDd.textContent = h; });
    g.draws.forEach(function (d, k) {
      var r = self.results[k];
      var round = roundFor(d.at);
      row(d.label + ' time', fmtWhen(d.at) + ' SAST');
      row(d.label + ' drand round', String(round) + (r ? '' : ' (not published yet)'), roundUrl(round));
      if (r) {
        row(d.label + ' randomness', r.randomness);
        row(d.label + ' pick', 'randomness mod ' + r.pool.length + ' = ' + r.poolIndex + ' → entry #' + (r.entryIndex + 1) + (k ? ' (earlier winners removed)' : ''));
      }
    });
    row('Beacon', 'drand quicknet (chain ' + CHAIN.slice(0, 12) + '…, every ' + PERIOD + ' s)', HOSTS[0] + '/' + CHAIN + '/info');
  };
  Card.prototype.markEntries = function () {
    var self = this;
    this.items.forEach(function (li) { li.classList.remove('is-win', 'is-out'); });
    var last = this.results.length - 1;
    this.results.forEach(function (r, k) {
      if (k < last) self.g.entries.forEach(function (e, idx) { if (e === r.entry && self.items[idx]) self.items[idx].classList.add('is-out'); });
    });
    var wl = this.results[last] && this.items[this.results[last].entryIndex];
    if (wl) wl.classList.add('is-win');
    var w = this.results[last] && this.items[this.results[last].entryIndex];
    if (w) this.ol.scrollTop = Math.max(0, w.offsetTop - this.ol.offsetTop - this.ol.clientHeight / 2);
  };
  Card.prototype.showWinner = function (k, celebrate) {
    var r = this.results[k], g = this.g;
    this.markEntries();
    this.renderFair();
    this.stage.hidden = false;
    this.winBox.textContent = '';
    var box = el('div', 'otcg-draw-win');
    box.appendChild(el('div', 'otcg-draw-win__k', k === 0 ? 'WINNER' : 'WINNER · ' + g.draws[k].label.toUpperCase()));
    box.appendChild(el('div', 'otcg-draw-win__n', r.entry));
    box.appendChild(el('div', 'otcg-draw-win__e', 'Entry #' + (r.entryIndex + 1) + ' of ' + g.entries.length));
    box.appendChild(el('p', 'otcg-draw-win__t', 'We announce the winner in the WhatsApp group and email them. The winner has 7 days to claim the prize.'));
    if (k > 0) {
      var prev = el('p', 'otcg-draw-win__prev', 'Earlier draw' + (k > 1 ? 's' : '') + ' (not claimed): ');
      for (var j = 0; j < k; j++) { if (j) prev.appendChild(document.createTextNode(', ')); prev.appendChild(el('s', null, this.results[j].entry)); }
      box.appendChild(prev);
    }
    this.winBox.appendChild(box);
    this.current.classList.add('otcg-draw-current--win');
    this.current.textContent = r.entry;
    this.replayBtn.hidden = false;
    this.setBadge('done', 'Drawn');
    this.when.textContent = g.draws[k].label + ' drawn ' + fmtWhen(g.draws[k].at) + ' SAST';
    this.say(k === 0 ? 'Congratulations!' : 'Congrats, new winner!');
    if (celebrate) { confetti(this.root); this.sound.fanfare(); }
  };
  Card.prototype.reveal = function (node, block) {
    var r = node.getBoundingClientRect();
    if (r.top < 0 || r.bottom > window.innerHeight) node.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: block });
  };
  Card.prototype.liveDraw = function (k) {
    var self = this, d = this.g.draws[k];
    this.winBox.textContent = '';
    this.current.classList.remove('otcg-draw-current--win');
    this.replayBtn.hidden = true;
    this.setBadge('live', 'Live draw');
    this.say('Here we go!!');
    this.countLabel.textContent = '';
    this.units.forEach(function (u) { u.parentNode.parentNode.hidden = true; });
    this.showStatus(k === 0 ? 'Drawing the winner…' : 'Drawing ' + d.label + '…', 'Waiting for the public random number from ' + fmtWhen(d.at));
    this.wheel.spinFast();
    this.reveal(this.wheelBox, 'center');
    return Promise.all([computeDraw(this.g, k, function () {
      self.showStatus('Waiting for the draw signal…', 'Still trying to reach the drand beacon. Keep this page open.');
    }), sleep(reduced ? 0 : 2500)]).then(function (all) {
      var r = all[0];
      self.results[k] = r;
      self.usePoolKeepAngle(r.pool);
      self.showStatus(k === 0 ? 'And the winner is…' : d.label + '…');
      return self.wheel.landOn(r.poolIndex).then(function () { return r; });
    }).then(function () {
      self.status.hidden = true;
      self.units.forEach(function (u) { u.parentNode.parentNode.hidden = false; });
      self.count.hidden = true;
      self.showWinner(k, true);
      self.reveal(self.winBox, 'nearest');
    });
  };
  Card.prototype.usePoolKeepAngle = function (pool) {
    if (pool.length !== this.pool.length) this.usePool(pool); else this.pool = pool;
  };
  Card.prototype.replay = function () {
    var self = this, k = this.results.length - 1;
    if (k < 0 || this.replaying) return;
    this.replaying = true;
    this.replayBtn.hidden = true;
    this.winBox.textContent = '';
    this.current.classList.remove('otcg-draw-current--win');
    this.usePool(this.results[k].pool);
    this.wheel.spinFast();
    this.reveal(this.wheelBox, 'center');
    this.say('Here we go!!');
    sleep(reduced ? 0 : 1600).then(function () { return self.wheel.landOn(self.results[k].poolIndex); }).then(function () {
      self.replaying = false;
      self.showWinner(k, true);
    });
  };
  // Someone can open the page before the list goes up and keep it open for the draw.
  Card.prototype.watchForList = function () {
    var g = this.g, sid = this.cfg.section_id;
    if (g.entries.length || g.drawAt == null || !sid) return;
    var iv = setInterval(function () {
      var now = Date.now();
      if (now < g.drawAt - 45 * 60 * 1000) return;
      if (now > g.drawAt + 2 * 3600 * 1000) { clearInterval(iv); return; }
      fetch(location.pathname + '?section_id=' + encodeURIComponent(sid) + '&t=' + Math.floor(now / 30000), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          var box = document.createElement('template');
          box.innerHTML = html;
          var s = box.content.querySelector('script[data-otcg-draw-giveaway]');
          if (s && String(JSON.parse(s.textContent).entries || '').trim()) location.reload();
        }).catch(function () {});
    }, 30000);
  };
  Card.prototype.run = async function () {
    var g = this.g;
    this.watchForList();
    if (!g.draws.length) { this.setBadge('', 'Date to be announced'); this.stage.hidden = true; return; }
    if (!g.entries.length) { this.setBadge('', 'Entries coming soon'); }
    for (var k = 0; k < g.draws.length; k++) {
      var d = g.draws[k];
      var live = false;
      if (Date.now() < d.at) {
        if (k > 0) { this.count.hidden = false; this.wheel.idle(); }
        await this.showCountdown(k);
        live = true;
      } else if (Date.now() - d.at < LIVE_GRACE_MS) {
        live = true;
      }
      if (!g.entries.length) { this.count.hidden = false; this.units.forEach(function (u) { u.parentNode.parentNode.hidden = true; }); this.countLabel.textContent = ''; this.showStatus('The list is on its way', 'This page updates by itself as soon as it goes up.'); return; }
      if (live) {
        await this.liveDraw(k);
      } else {
        this.count.hidden = false;
        this.units.forEach(function (u) { u.parentNode.parentNode.hidden = true; });
        this.countLabel.textContent = '';
        this.showStatus('Loading the result…', '');
        var self = this;
        var r = await computeDraw(g, k, function () { self.showStatus('Waiting for the draw signal…', 'Still trying to reach the drand beacon.'); });
        this.results[k] = r;
        this.usePool(r.pool);
        this.wheel.stopAt(r.poolIndex);
        this.status.hidden = true;
        this.units.forEach(function (u) { u.parentNode.parentNode.hidden = false; });
        this.count.hidden = true;
        this.showWinner(k, false);
      }
    }
  };

  /* ---------- boot ---------- */
  function init(root) {
    if (root.getAttribute('data-otcg-draw-ready')) return;
    root.setAttribute('data-otcg-draw-ready', '1');
    var cfg = {};
    try { cfg = JSON.parse((root.querySelector('[data-otcg-draw-config]') || {}).textContent || '{}'); } catch (e) { cfg = {}; }
    var list = root.querySelector('[data-otcg-draw-list]');
    var gs = [];
    root.querySelectorAll('script[data-otcg-draw-giveaway]').forEach(function (s, i) {
      try { gs.push(parseGiveaway(JSON.parse(s.textContent), i)); } catch (e) { if (window.console) console.warn('Giveaway block ' + (i + 1) + ' has invalid data', e); }
    });
    list.textContent = '';
    if (typeof BigInt === 'undefined' || !window.fetch || !window.Promise) {
      list.appendChild(el('p', 'otcg-draw__empty', 'Your browser is too old to run the live draw. Please open this page in an up-to-date browser.'));
      return;
    }
    var now = Date.now();
    var featured = gs.filter(function (g) { return g.lastAt == null || g.lastAt > now - FEATURE_MS; });
    var past = gs.filter(function (g) { return featured.indexOf(g) === -1; }).sort(function (a, b) { return b.lastAt - a.lastAt; });
    if (!featured.length && past.length) featured.push(past.shift());
    // Draw day: the wheel moves to the top of the page.
    var soon = gs.some(function (g) { return g.drawAt != null && now > g.drawAt - 3 * 3600 * 1000 && now < g.drawAt + FEATURE_MS; });
    var sec = root.closest('.shopify-section');
    if (soon && sec && sec.parentNode && sec.parentNode.firstElementChild !== sec) sec.parentNode.insertBefore(sec, sec.parentNode.firstElementChild);
    function nextAt(g) {
      for (var i = 0; i < g.draws.length; i++) if (g.draws[i].at > now) return g.draws[i].at;
      return null;
    }
    featured.sort(function (a, b) {
      var na = nextAt(a), nb = nextAt(b);
      if (na != null && nb != null) return na - nb;
      if (na != null) return -1;
      if (nb != null) return 1;
      return (b.lastAt || 0) - (a.lastAt || 0);
    });
    if (!gs.length) list.appendChild(el('p', 'otcg-draw__empty', 'No draw right now. The next one is announced in the WhatsApp group.'));
    var sound = new Sound();
    featured.forEach(function (g) {
      var card = new Card(g, cfg, sound);
      list.appendChild(card.root);
      card.run().catch(function (e) { if (window.console) console.error(e); });
    });
    // A drawn giveaway past its 48 hours stays off the wheel; the page's Past giveaways list keeps the record.
  }
  function boot() { document.querySelectorAll('[data-otcg-draw]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  window.OTCGDraw = { roundFor: roundFor, pickIndex: pickIndex, getRound: getRound, GENESIS: GENESIS, PERIOD: PERIOD, CHAIN: CHAIN };
})();
